import { Edge, Node } from '@xyflow/react';
import { IVlanItem } from 'src/context/global/types';
import NodeData, {
  EDataType,
  NodeDataType,
  NodeCategoryModel,
} from 'src/modules/network-configuration/models/node-data';
import {
  IConfigurationLease,
  IConfigurationContentData,
} from 'src/types/configuration';
import { calculateNetworkAddress2 } from 'src/utils/ip';
import { v4 as uuidv4 } from 'uuid';

import { IDecodedCfgFile } from '../types';
import {
  VLANS_COLORS,
  AUTO_DEPLOY_IP,
  RESERVED_AUTO_DEPLOY_VLAN,
} from './import-topology-shared';
import { DeviceIpByNodeAndVlan } from './import-topology-types';

export type ImportContent = {
  uniqueId: string;
  contentId: string;
  contentName: string;
  data: IConfigurationContentData;
};

export const safeContent = (
  file: IDecodedCfgFile<IConfigurationContentData>,
): ImportContent[] => {
  const contents = file.data?.['roqs-container']?.['content-list'];
  const contentFiltered = contents?.filter(
    (content) => content['content-data'],
  );
  if (!contentFiltered || !contentFiltered.length) return [];
  return contentFiltered.map((content) => ({
    uniqueId: uuidv4(),
    contentId: content['content-id'],
    contentName: content['content-name'],
    data: content['content-data'] as IConfigurationContentData,
  }));
};

export const toSwitchType = (contentId: string): NodeDataType => {
  const candidate = contentId as NodeDataType;
  if (candidate in NodeData && NodeData[candidate]?.type === EDataType.Switch) {
    return candidate;
  }
  return NodeDataType.Switch006130105;
};

export const isReservedAutoDeployVlan = (vid: number) =>
  vid === RESERVED_AUTO_DEPLOY_VLAN;

const emptyIpInterface = (vlanId: number) => ({
  vlanId,
  ip: '',
  subnet: '',
  subnetMask: '',
});

export const mergeIpInterfaces = (
  existing: NodeCategoryModel['ipInterfaces'] | undefined,
  vlanIds: number[],
) => {
  const map = new Map<
    number,
    NonNullable<NodeCategoryModel['ipInterfaces']>[number]
  >();
  (existing ?? []).forEach((it) => map.set(it.vlanId, it));
  vlanIds.forEach((vid) => {
    if (!map.has(vid)) map.set(vid, emptyIpInterface(vid));
  });
  return [...map.values()].sort((a, b) => a.vlanId - b.vlanId);
};

export function collectVlansAndSubnet(contents: ImportContent[]) {
  const vlanMeta = new Map<
    number,
    { description: string; ip?: string; subnetMask?: string; ipSubnet?: string }
  >();

  contents.forEach((c) => {
    // check has vlan_list in data.vlan
    if ((c.data.vlan?.bridge as any)?.vlan_list) {
      (c.data.vlan?.bridge as any).vlan_list.forEach(
        (vlan: {
          dhcp_client: boolean;
          dhcp_filter: boolean;
          dhcp_provision: boolean;
          forward: boolean;
          gw: string;
          ip: string;
          name: string;
          proxy_arp: boolean;
          sm: string;
          vlan_id: number;
        }) => {
          if (vlan.vlan_id === 0 || isReservedAutoDeployVlan(vlan.vlan_id))
            return;
          vlanMeta.set(vlan.vlan_id, {
            ip: vlan.ip,
            subnetMask: vlan.sm,
            ipSubnet: calculateNetworkAddress2(vlan.ip, vlan.sm),
            description: vlan.name,
          });
        },
      );
    }
    // Some exports omit vlan.vids; dhcp.vids still provides VLAN ids (server/off).
    (c.data.dhcp?.vids ?? []).forEach((dv) => {
      if (!dv?.vid || isReservedAutoDeployVlan(dv.vid)) return;
      if (!vlanMeta.has(dv.vid)) {
        vlanMeta.set(dv.vid, { description: `VLAN ${dv.vid}` });
      }
    });

    (c.data.vlan?.vids ?? []).forEach((v) => {
      if (isReservedAutoDeployVlan(v.vid)) return;
      if (!vlanMeta.has(v.vid)) {
        vlanMeta.set(v.vid, { description: v.name || `VLAN ${v.vid}` });
      } else if (v.name) {
        vlanMeta.set(v.vid, { description: v.name });
      }
    });

    (c.data.vlan?.bridges ?? []).forEach((b) => {
      if (b.vid === 0 || isReservedAutoDeployVlan(b.vid)) return;
      const current = vlanMeta.get(b.vid) ?? { description: `VLAN ${b.vid}` };
      if (b.ip && b.sm) {
        current.ip = b.ip;
        current.subnetMask = b.sm;
        current.ipSubnet = calculateNetworkAddress2(b.ip, b.sm);
      }
      vlanMeta.set(b.vid, current);
    });
  });

  const vlans = [...vlanMeta.entries()]
    .sort(([a], [b]) => a - b)
    .map(([vlanId, meta], index) => ({
      id: uuidv4(),
      color: VLANS_COLORS[index % VLANS_COLORS.length],
      vlanId,
      description: meta.description,
      ipSubnet: meta.ipSubnet,
      subnetMask: meta.subnetMask,
      ip: meta.ipSubnet,
    }));

  const useCustomVlan = vlans.length > 0;
  const globalIpSubnet = !useCustomVlan
    ? getGlobalIpSubnet(contents)
    : undefined;

  return { vlans, useCustomVlan, globalIpSubnet };
}

function getGlobalIpSubnet(contents: ImportContent[]) {
  const b0 =
    contents[0]?.data.vlan?.bridges?.find((b) => b.vid === 0) ??
    contents[0]?.data.vlan?.bridges?.[0];
  if (!b0?.ip || !b0?.sm) return undefined;
  const ipSubnet = calculateNetworkAddress2(b0.ip, b0.sm);
  const globalIpSubnet = { ip: ipSubnet, ipSubnet, subnetMask: b0.sm };

  const listIps: string[] = [];

  contents.forEach((c) => {
    c.data.vlan?.bridges?.forEach((b) => {
      if (b.ip === AUTO_DEPLOY_IP) return;
      listIps.push(b.ip);
    });
  });

  // Get IP from dhcp.lease and dhcp.subnets
  contents.forEach((c) => {
    c.data.dhcp?.lease?.forEach((l) => {
      if (l.ip === AUTO_DEPLOY_IP) return;
      listIps.push(l.ip);
    });
    c.data.dhcp?.subnets?.forEach((s) => {
      if (s.ip === AUTO_DEPLOY_IP) return;
      listIps.push(s.ip);
    });
  });

  // check all listIps in globalIpSubnet
  const allInSubnet = listIps.filter(Boolean).every((ip) => {
    try {
      const network = calculateNetworkAddress2(ip, globalIpSubnet.subnetMask);
      return network === globalIpSubnet.ipSubnet;
    } catch {
      return false;
    }
  });

  if (!allInSubnet) return undefined;

  return globalIpSubnet;
}

export const invertPortIndexes = (portIndexes: Record<string, number>) => {
  const inv = new Map<number, string>();
  Object.entries(portIndexes).forEach(([portId, idx]) => inv.set(idx, portId));
  return inv;
};

const vlanIdFromDhcpLease = (lease: IConfigurationLease): number => {
  const vid = lease?.value?.[0];
  return typeof vid === 'number' ? vid : 0;
};

/** In DHCP leases, port is stored as (portIndex - 1). Convert to portIndex. */
const portIndexFromDhcpLease = (lease: IConfigurationLease): number => {
  const raw = lease?.value?.[1];
  return typeof raw === 'number' ? raw + 1 : 0;
};

/**
 * Build map: portIndex -> vlanId -> ip from dhcp.lease.
 * (VLAN 3579 is ignored by caller, but we also defensively keep it out.)
 */
export const buildDhcpLeaseIpMap = (leases: IConfigurationLease[]) => {
  const byPort = new Map<number, Map<number, string>>();
  (leases ?? []).forEach((lease) => {
    const portIndex = portIndexFromDhcpLease(lease);
    const vlanId = vlanIdFromDhcpLease(lease);
    if (!portIndex || isReservedAutoDeployVlan(vlanId)) return;
    const ip = lease.ip || '';
    if (!ip) return;
    const map = byPort.get(portIndex) ?? new Map<number, string>();
    map.set(vlanId, ip);
    byPort.set(portIndex, map);
  });
  return byPort;
};

export function layoutSwitchesAndDevices({
  switchNodes,
  deviceNodes,
  edges,
}: {
  switchNodes: Node[];
  deviceNodes: Node[];
  edges: Edge[];
}) {
  const switchGapX = 920;
  const switchY = 60;
  const devicesYBase = 420;
  const colCount = 3;
  const colGap = 220;
  const rowGap = 160;
  const baseSwitchX = 120;

  switchNodes.forEach((sw, idx) => {
    sw.position.x = baseSwitchX + idx * switchGapX;
    sw.position.y = switchY;
  });

  const switchIds = new Set(switchNodes.map((s) => s.id));
  const deviceIds = new Set(deviceNodes.map((d) => d.id));

  const devicesBySwitch = new Map<string, string[]>();
  edges.forEach((e) => {
    if (!switchIds.has(e.target) || !deviceIds.has(e.source)) return;
    const list = devicesBySwitch.get(e.target) ?? [];
    if (!list.includes(e.source)) list.push(e.source);
    devicesBySwitch.set(e.target, list);
  });

  const deviceById = new Map(deviceNodes.map((d) => [d.id, d]));
  switchNodes.forEach((sw) => {
    const devs = devicesBySwitch.get(sw.id) ?? [];
    devs.forEach((devId, i) => {
      const dev = deviceById.get(devId);
      if (!dev) return;
      const col = i % colCount;
      const row = Math.floor(i / colCount);
      dev.position.x = sw.position.x + (col - 1) * colGap;
      dev.position.y = devicesYBase + row * rowGap;
    });
  });
}

export function applyDeviceVlansAndIps({
  deviceNodes,
  deviceVlans,
  vlans,
  deviceIpByNodeAndVlan,
}: {
  deviceNodes: Node[];
  deviceVlans: Map<string, Set<number>>;
  vlans: IVlanItem[];
  deviceIpByNodeAndVlan: Map<string, DeviceIpByNodeAndVlan>;
}) {
  deviceNodes.forEach((n) => {
    const category = n.data.category as NodeCategoryModel;
    const ipByVlan = deviceIpByNodeAndVlan.get(n.id);
    const set = deviceVlans.get(n.id);

    if (set?.size) {
      category.ipInterfaces = mergeIpInterfaces(category.ipInterfaces, [
        ...set,
      ]);
    }
    category.ipInterfaces?.forEach((it) => {
      const vlan = vlans.find((v) => v.vlanId === it.vlanId);
      if (vlan?.ipSubnet) it.subnet = vlan.ipSubnet;
      if (vlan?.subnetMask) it.subnetMask = vlan.subnetMask;
    });
    if (!ipByVlan) return;
    const ip0 = ipByVlan.get(0);
    if (ip0) category.ip = ip0.ip;
    category.ipInterfaces?.forEach((it) => {
      const ip = ipByVlan.get(it.vlanId);
      if (ip) it.ip = ip.ip;
      if (ip) it.dhcp = ip.dhcp;
    });
  });
}
